import {FARE_TYPES, FINANCING_MODES, LOAN_URL_POLLING} from './config.js'

const resolvePlateRegistrationDate = ({plateRegistrationDate, year}) => plateRegistrationDate || `01/${year}`

const wait = time => new Promise(resolve => setTimeout(resolve, time))

/**
 * Creating a b2c loan request and getting somewhere to redirect to are two different
 * moments: `create_loan_id` returns as soon as the request is registered, while the URL
 * only materializes once the lender has processed it. Until then the endpoint answers
 * `200` with an empty url — so the URL has to be polled for, not read once.
 *
 * `null` means the lender never produced one within the budget, which the CTA surfaces as
 * its error state.
 */
const retrieveLoanUrl = async ({currentRetry = 0, domain, loanId}) => {
  if (currentRetry >= LOAN_URL_POLLING.MAX_RETRIES) return null

  await wait(LOAN_URL_POLLING.RETRY_INTERVAL * currentRetry)

  const [error, loanUrl] = await domain.get('get_loan_url_b2c_transactions_use_case').execute({loanId})
  if (error) return null

  return loanUrl || retrieveLoanUrl({currentRetry: currentRetry + 1, domain, loanId})
}

/**
 * Folds a fetched calculation into the single shape the rest of the panel reads:
 * `{cashPrice, insuranceCoverages, fares, raw}`. `fares` always carries at least one
 * entry, so nothing downstream needs to know whether the ad has one fare or two.
 * `raw` keeps the un-normalized payload around for the CTA use cases, which expect it.
 *
 * The discriminator is `fareType`, never `id`: the lender payload spread into each fare
 * carries its own `id`, so reusing that key would silently overwrite the fare type.
 */
const buildSingleFare = raw => [{...raw, fareType: FARE_TYPES.WITHOUT_INSURANCE}]

/**
 * One entry per financing flavour. Everything that differs between the private-seller
 * personal loan (c2c), the dealer FDA offer with its dual fares (b2c) and the static
 * display (simplified) lives here: which use case to call, how to normalize its response,
 * which literals to render, and how the CTA resolves its redirect URL. Keeping it in one
 * table is what lets `index.js` and `useFinancingCalculations` stay mode-agnostic.
 */
export const FINANCING_STRATEGIES = {
  [FINANCING_MODES.C2C]: {
    isInteractive: true,
    showsFareSelector: false,
    titleKey: 'PANEL_FINANCING.REVAMP.C2C.TITLE',
    titleShortKey: 'PANEL_FINANCING.REVAMP.C2C.TITLE_SHORT',
    ctaKey: 'GET_PERSONAL_LOAN',
    entryFieldKey: 'FINANCING_INFO.CASH_PAYMENT',
    feeLabelKey: 'COMMON.FEE',
    claimKeys: ['C2C.CLAIM_TEXT_1', 'C2C.CLAIM_TEXT_2'],

    calculationsUseCase: 'get_loan_calculations_transactions_use_case',
    buildCalculationsParams: ({ad, entry, financingData, terms}) => ({
      carPlateRegistrationDate: resolvePlateRegistrationDate(ad),
      lender: financingData?.lender,
      openingFee: entry != null ? Number(entry) : financingData?.minEntry,
      price: ad.price,
      terms: terms ?? financingData?.terms
    }),
    normalize: ({ad, financingData, response}) => {
      const raw = {...financingData, ...response}
      return {
        cashPrice: raw.cashPrice ?? ad.price,
        insuranceCoverages: raw.insuranceCoverages,
        fares: buildSingleFare(raw),
        raw
      }
    },
    resolveCtaUrl: async ({ad, data, domain, entryValue, selectedTerms, vehicleInfo}) => {
      const [error, response] = await domain.get('get_loan_url_transactions_use_case').execute({
        ...data.raw,
        adId: ad.id,
        entry: entryValue,
        make: ad.make,
        model: ad.model,
        plateRegistrationDate: resolvePlateRegistrationDate(ad),
        price: ad.price,
        terms: selectedTerms,
        version: ad.version,
        vehicleId: vehicleInfo.vehicleId
      })
      return error ? null : response?.url ?? null
    }
  },

  [FINANCING_MODES.B2C]: {
    isInteractive: true,
    showsFareSelector: true,
    titleKey: 'PANEL_FINANCING.REVAMP.B2C.TITLE',
    titleShortKey: 'PANEL_FINANCING.REVAMP.B2C.TITLE_SHORT',
    ctaKey: 'PANEL_FINANCING.REVAMP.B2C.CTA',
    entryFieldKey: 'PANEL_FINANCING.B2C.ENTRY_INPUT_LABEL',
    feeLabelKey: 'COMMON.FEE_FROM',
    claimKeys: ['PANEL_FINANCING.REVAMP.B2C.CLAIM_1', 'PANEL_FINANCING.REVAMP.B2C.CLAIM_2'],

    calculationsUseCase: 'get_loan_calculations_B2C_transactions_use_case',
    buildCalculationsParams: ({ad, entry, financingData, terms, withLifeInsurance = false}) => ({
      adId: ad.id,
      entry: entry != null ? Number(entry) : financingData?.minEntry,
      lender: financingData?.lender,
      terms: terms ?? financingData?.terms,
      withLifeInsurance
    }),
    // A single call returns both fares keyed by FARE_TYPES; a fare with no fee is one
    // the lender did not quote, so it is dropped rather than rendered empty.
    normalize: ({financingData, response}) => ({
      cashPrice: financingData?.cashPrice,
      insuranceCoverages: financingData?.insuranceCoverages,
      fares: [FARE_TYPES.WITHOUT_INSURANCE, FARE_TYPES.WITH_INSURANCE]
        .map(fareType => ({...response[fareType], fareType}))
        .filter(({fee}) => fee != null),
      raw: {...financingData, calculatorB2cData: response}
    }),
    resolveCtaUrl: async ({ad, currentFare, data, domain, entryValue, selectedFare, selectedTerms}) => {
      const [createError, loanId] = await domain.get('create_loan_id_b2c_transactions_use_case').execute({
        adId: ad.id,
        entry: Number(entryValue) || 0,
        fee: currentFare?.fee,
        terms: selectedTerms,
        withLifeInsurance: selectedFare === FARE_TYPES.WITH_INSURANCE,
        // The quoted fare's own coverages win over the ones advertised on the ad — legacy
        // merges the chosen fare over `financingData`, so after any re-quote the ad-level
        // value is stale.
        withGapInsurance: currentFare?.insuranceCoverages?.gap ?? data.insuranceCoverages?.gap
      })
      if (createError || !loanId) return null

      return retrieveLoanUrl({domain, loanId})
    }
  },

  [FINANCING_MODES.SIMPLIFIED]: {
    isInteractive: false,
    showsFareSelector: false,
    titleKey: 'PANEL_FINANCING.REVAMP.SIMPLIFIED.TITLE',
    titleShortKey: 'PANEL_FINANCING.REVAMP.SIMPLIFIED.TITLE_SHORT',
    feeLabelKey: 'COMMON.FEE_FROM',
    claimKeys: [],
    contactSellerCtaKey: 'PANEL_FINANCING.CONTACT_SELLER_CTA',

    calculationsUseCase: null,
    normalize: ({financingData}) => ({
      cashPrice: financingData?.cashPrice,
      insuranceCoverages: financingData?.insuranceCoverages,
      fares: buildSingleFare(financingData),
      raw: financingData
    })
  }
}
